import React from "react";
import {useParams, Prompt} from "react-router-dom";
import {useTranslation, Trans} from "react-i18next";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import {RiImageLine, RiDeleteBinLine, RiPushpinLine, RiCloseLine, RiAlertFill, RiShieldLine} from "react-icons/ri";
import {getCampaign, saveGroupSettings, getGroupPhoto, getCampaignPinned, getGroupState} from "../../../Services/campaignFunctions";
import {initials, avatarColorFor, groupPhotoUrl} from "../../helpers/avatar";
import {useGroupManager} from "../../Providers/groupManager";
import ConfirmDialog from "../../Components/ConfirmDialog";
import Toggle from "../../Components/Toggle";
import {
    Container, State, Banner, WarnIcon, BannerText, BannerTitle, BannerBody, Card, CardHead, CardTitle, Badge, IdentityRow, PhotoCol, Avatar, PhotoBtn, RemoveBtn,
    HiddenFileInput, Fields, Field, Label, InputWrap, Input, Counter, TextareaCounter, MetaRow, Hint, Textarea,
    PinnedHead, PinnedIcon, PinnedHeadText, CardSubtitle, PinnedList, PinnedItem, PinnedFrom, PinnedText, PinnedEmpty,
    BehaviorRow, BehaviorInfo, BehaviorName, BehaviorDesc, GroupStateHint, WelcomeNote,
    Footer, FooterNote, Actions, CancelBtn, SaveBtn,
} from "./styles";

const NAME_MAX = 35;
const DESC_MAX = 512;
const MAX_BYTES = 5 * 1024 * 1024;
const VALID_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Aba Group Settings — Identidade (nome + descrição + FOTO). Config NÍVEL CAMPANHA:
 * salva na campanha e propaga aos grupos (job assíncrono). Pinned/Behavior = placeholder.
 */
const GroupSettings = () => {
    const {t} = useTranslation();
    const {id: campaignId} = useParams();
    const {selectedGroup, can, campaign} = useGroupManager();
    const [snack, setSnack] = React.useState("");
    const [discardOpen, setDiscardOpen] = React.useState(false);
    const [groupAnnounce, setGroupAnnounce] = React.useState(null); // estado real (announce) do grupo selecionado
    const [saved, setSaved] = React.useState(null);
    const [form, setForm] = React.useState({group_name: "", group_description: "", group_image: "", welcome_enabled: false, welcome_message: "", admins_only: true});
    const [waPhoto, setWaPhoto] = React.useState(null); // foto REAL do grupo no WhatsApp (url) ou null
    const [pinned, setPinned] = React.useState([]); // mensagens fixadas do 1º grupo (read-only)
    const [photoAction, setPhotoAction] = React.useState(null); // null | {file, preview} | {remove: true}
    const [photoError, setPhotoError] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const fileRef = React.useRef(null);

    const load = React.useCallback(async () => {
        const c = await getCampaign(campaignId);
        if (!c || c.isAxiosError || c instanceof Error) {
            setError(true);
            return null;
        }
        const v = {
            group_name: c.group_name || "",
            group_description: c.group_description || "",
            group_image: c.group_image || "",
            welcome_enabled: !!c.welcome_enabled,
            welcome_message: c.welcome_message || "",
            admins_only: c.admins_only === undefined ? true : !!c.admins_only,
        };
        setSaved(v);
        setForm(v);
        const p = await getGroupPhoto(campaignId);
        setWaPhoto(p && !p.isAxiosError && !(p instanceof Error) ? (p.url || null) : null);
        const pins = await getCampaignPinned(campaignId);
        setPinned(Array.isArray(pins) ? pins : []);
        return v;
    }, [campaignId]);

    React.useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(false);
        (async () => {
            await load();
            if (alive) setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [load]);

    const clearPhotoAction = React.useCallback(() => {
        setPhotoAction((pa) => {
            if (pa?.preview) URL.revokeObjectURL(pa.preview);
            return null;
        });
        setPhotoError("");
    }, []);

    const dirty = saved && (
        form.group_name !== saved.group_name ||
        form.group_description !== saved.group_description ||
        form.welcome_enabled !== saved.welcome_enabled ||
        form.welcome_message !== saved.welcome_message ||
        form.admins_only !== saved.admins_only ||
        photoAction != null
    );
    const canEdit = can("can_edit_group");
    const canSave = canEdit && !!dirty && !saving && form.group_name.trim() !== "" && form.group_description.trim() !== "";

    // Aviso ao fechar/refresh a aba com mudanças não salvas (o <Prompt> cobre navegação SPA).
    React.useEffect(() => {
        if (!dirty) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    // Estado real (announce) do grupo selecionado — indicador informativo (não mexe no form/dirty).
    React.useEffect(() => {
        setGroupAnnounce(null); // limpa o stale antes do novo fetch
        if (!selectedGroup) return undefined;
        let alive = true;
        (async () => {
            const res = await getGroupState(campaignId, selectedGroup.id);
            if (!alive) return;
            if (!(res?.isAxiosError || res instanceof Error) && res && typeof res.announce === "boolean") {
                setGroupAnnounce(res.announce);
            }
        })();
        return () => { alive = false; };
    }, [campaignId, selectedGroup]);

    const onPickFile = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // permite re-selecionar o mesmo arquivo
        if (!file) return;
        if (!VALID_TYPES.includes(file.type)) {
            setPhotoError(t("words.photo_invalid_type"));
            return;
        }
        if (file.size > MAX_BYTES) {
            setPhotoError(t("words.photo_too_large"));
            return;
        }
        setPhotoError("");
        setPhotoAction((pa) => {
            if (pa?.preview) URL.revokeObjectURL(pa.preview);
            return {file, preview: URL.createObjectURL(file)};
        });
    };

    const onRemovePhoto = () => {
        setPhotoAction((pa) => {
            if (pa?.preview) URL.revokeObjectURL(pa.preview);
            return {remove: true};
        });
        setPhotoError("");
    };

    // Exibição prioriza: preview local (ao escolher) > remove (iniciais) > foto REAL do WhatsApp >
    // imagem configurada da campanha (group_image, mesma dos outros avatares) > iniciais.
    const avatarImage = photoAction?.preview
        ? photoAction.preview
        : (photoAction?.remove ? null : (waPhoto || groupPhotoUrl(form)));
    const hasPhoto = !!avatarImage;

    const onCancel = () => {
        if (!dirty) return;
        setDiscardOpen(true);
    };

    const confirmDiscard = () => {
        setDiscardOpen(false);
        if (saved) setForm(saved);
        clearPhotoAction();
    };

    const onSave = async () => {
        if (!canSave) return;
        setSaving(true);
        const res = await saveGroupSettings(campaignId, {
            group_name: form.group_name.trim(),
            group_description: form.group_description,
            image: photoAction?.file,
            removePhoto: photoAction?.remove,
            welcome_enabled: form.welcome_enabled,
            welcome_message: form.welcome_message,
            admins_only: form.admins_only,
        });
        setSaving(false);
        if (res?.isAxiosError || res instanceof Error) {
            setSnack(t("words.something_wrong"));
            return;
        }

        // Sucesso: limpa o "dirty" e mostra a foto escolhida de forma OTIMISTA — a foto real do
        // WhatsApp só troca após a propagação (~5min), então não refazemos o fetch aqui.
        const trimmed = {
            group_name: form.group_name.trim(),
            group_description: form.group_description,
            // Ao remover a foto, zera o group_image — senão o fallback groupPhotoUrl(form) traria
            // a imagem removida de volta no avatar (e reativaria o Remove).
            group_image: photoAction?.remove ? "" : form.group_image,
            welcome_enabled: form.welcome_enabled,
            welcome_message: form.welcome_message,
            admins_only: form.admins_only,
        };
        setSaved(trimmed);
        setForm(trimmed);
        setSnack(t("words.changes_saved_successfully"));
        if (photoAction?.preview) {
            setWaPhoto(photoAction.preview); // mantém a imagem escolhida (não revoga o objectURL)
            setPhotoAction(null);
        } else if (photoAction?.remove) {
            setWaPhoto(null);
            clearPhotoAction();
        } else {
            clearPhotoAction();
        }
    };

    if (loading) return <Container><State>{t("words.loading")}</State></Container>;
    if (error) return <Container><State>{t("words.something_wrong")}</State></Container>;

    return (
        <Container>
            <Banner>
                <WarnIcon><RiAlertFill/></WarnIcon>
                <BannerText>
                    <BannerTitle>{t("words.campaign_wide_changes_title")}</BannerTitle>
                    <BannerBody>
                        <Trans
                            i18nKey="words.campaign_wide_changes_body"
                            values={{count: campaign?.group_count || 0, name: campaign?.name || ""}}
                            components={{b: <b/>}}
                        />
                    </BannerBody>
                </BannerText>
            </Banner>

            <Card>
                <CardHead>
                    <PinnedHeadText>
                        <CardTitle>{t("words.group_identity")}</CardTitle>
                        <CardSubtitle>{t("words.group_identity_subtitle")}</CardSubtitle>
                    </PinnedHeadText>
                    <Badge><RiShieldLine/> {t("words.admin_privileges_required")}</Badge>
                </CardHead>
                <IdentityRow>
                    <PhotoCol>
                        <Avatar data-testid="gs-avatar" $color={avatarColorFor(campaignId)} $image={avatarImage}>
                            {!avatarImage && initials(form.group_name || "G")}
                        </Avatar>
                        <HiddenFileInput
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={onPickFile}
                        />
                        <PhotoBtn type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={!canEdit}>
                            <RiImageLine/> {t("words.replace_photo")}
                        </PhotoBtn>
                        <RemoveBtn type="button" onClick={onRemovePhoto} disabled={!hasPhoto || !canEdit}>
                            <RiDeleteBinLine/> {t("words.remove")}
                        </RemoveBtn>
                        {photoError ? <Hint style={{color: "#EF4444"}}>{photoError}</Hint> : null}
                    </PhotoCol>
                    <Fields>
                        <Field>
                            <Label>{t("words.group_Name")}</Label>
                            <InputWrap>
                                <Input
                                    value={form.group_name}
                                    maxLength={NAME_MAX}
                                    disabled={!canEdit}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setForm((f) => ({...f, group_name: val}));
                                    }}
                                />
                                <Counter>{form.group_name.length}/{NAME_MAX}</Counter>
                            </InputWrap>
                            <Hint>{t("words.group_name_hint")}</Hint>
                        </Field>
                        <Field>
                            <Label>{t("words.description")}</Label>
                            <Textarea
                                value={form.group_description}
                                maxLength={DESC_MAX}
                                rows={2}
                                disabled={!canEdit}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setForm((f) => ({...f, group_description: val}));
                                }}
                            />
                            <MetaRow>
                                <Hint>{t("words.changes_apply_delay")}</Hint>
                                <TextareaCounter>{form.group_description.length}/{DESC_MAX}</TextareaCounter>
                            </MetaRow>
                        </Field>
                    </Fields>
                </IdentityRow>
            </Card>

            <Card>
                <PinnedHead>
                    <PinnedIcon><RiPushpinLine/></PinnedIcon>
                    <PinnedHeadText>
                        <CardTitle>{t("words.pinned_message")}</CardTitle>
                        <CardSubtitle>{t("words.pinned_message_desc")}</CardSubtitle>
                    </PinnedHeadText>
                </PinnedHead>
                {pinned.length === 0 ? (
                    <PinnedEmpty>{t("words.no_pinned_messages")}</PinnedEmpty>
                ) : (
                    <PinnedList>
                        {pinned.map((m) => (
                            <PinnedItem key={m.id}>
                                <PinnedFrom>{m.from_me ? t("words.you") : (m.from?.name || m.from?.jid?.split("@")[0] || "")}</PinnedFrom>
                                <PinnedText>{m.content}</PinnedText>
                            </PinnedItem>
                        ))}
                    </PinnedList>
                )}
            </Card>

            <Card>
                <CardTitle>{t("words.behavior")}</CardTitle>
                <BehaviorRow>
                    <BehaviorInfo>
                        <BehaviorName>{t("words.welcome_message")}</BehaviorName>
                        <BehaviorDesc>{t("words.welcome_message_desc")}</BehaviorDesc>
                    </BehaviorInfo>
                    <Toggle
                        aria-label={t("words.welcome_message")}
                        checked={form.welcome_enabled}
                        disabled={!canEdit}
                        onChange={(e) => {
                            const v = e.target.checked;
                            setForm((f) => ({...f, welcome_enabled: v}));
                        }}
                    />
                </BehaviorRow>
                {form.welcome_enabled ? (
                    <>
                        <Textarea
                            value={form.welcome_message}
                            maxLength={1024}
                            rows={3}
                            placeholder={t("words.welcome_message_placeholder")}
                            disabled={!canEdit}
                            onChange={(e) => {
                                const v = e.target.value;
                                setForm((f) => ({...f, welcome_message: v}));
                            }}
                        />
                        <WelcomeNote><WarnIcon $sm><RiAlertFill/></WarnIcon><span>{t("words.welcome_anti_ban_note")}</span></WelcomeNote>
                    </>
                ) : null}
                <BehaviorRow>
                    <BehaviorInfo>
                        <BehaviorName>{t("words.admins_only")}</BehaviorName>
                        <BehaviorDesc>{t("words.admins_only_desc")}</BehaviorDesc>
                        {groupAnnounce !== null && (
                            <GroupStateHint>
                                {groupAnnounce ? t("words.group_state_admins_only") : t("words.group_state_open")}
                            </GroupStateHint>
                        )}
                    </BehaviorInfo>
                    <Toggle
                        aria-label={t("words.admins_only")}
                        checked={form.admins_only}
                        disabled={!canEdit}
                        onChange={(e) => {
                            const v = e.target.checked;
                            setForm((f) => ({...f, admins_only: v}));
                        }}
                    />
                </BehaviorRow>
            </Card>

            <Footer>
                <FooterNote>{t("words.unsaved_changes")}</FooterNote>
                <Actions>
                    <CancelBtn type="button" onClick={onCancel} disabled={!dirty}>{t("words.cancel")}</CancelBtn>
                    <SaveBtn type="button" onClick={onSave} disabled={!canSave}>{t("words.save")}</SaveBtn>
                </Actions>
            </Footer>

            <Prompt when={!!dirty} message={t("words.unsaved_changes")}/>
            <ConfirmDialog
                open={discardOpen}
                title={t("words.discard_changes")}
                message={t("words.discard_changes_confirm")}
                confirmLabel={t("words.discard_changes")}
                danger
                onConfirm={confirmDiscard}
                onCancel={() => setDiscardOpen(false)}
            />
            <Snackbar
                open={!!snack}
                onClose={(e, reason) => { if (reason !== "clickaway") setSnack(""); }}
                message={snack}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                action={
                    <IconButton size="small" color="inherit" onClick={() => setSnack("")} aria-label={t("words.close")}>
                        <RiCloseLine/>
                    </IconButton>
                }
            />
        </Container>
    );
};

export default GroupSettings;
